import React, { useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AppLayout from "layouts/AppLayout";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import { ROUTES } from "config/navConfig";
import { useRole } from "context/RoleContext";
import { ROLES } from "config/roleConfig";
import { MOCK_SPVS, MOCK_ASSETS, MOCK_FUNDS } from "data/mockData";
import QuickActionsPanel from "components/ui/QuickActionsPanel";
import MetricCard from "./components/MetricCard";
import ClosingProgress from "./components/ClosingProgress";
import CommitmentsPanel from "./components/CommitmentsPanel";
import DocumentsPanel from "./components/DocumentsPanel";
import TasksPanel from "./components/TasksPanel";
import SPVHeader from "./components/SPVHeader";
import ExportModal from "./components/ExportModal";
import ToastNotification from "./components/ToastNotification";

// ─── Main Component ───────────────────────────────────────────────────────────
export default function SPVDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { role, activeContext } = useRole();
  const isCoInvestor = role === ROLES.CO_INVESTOR || role === ROLES.EXTERNAL_INVESTOR;

  // ─── Active SPV Resolution ─────────────────────────────────────────────────
  // 1. Explicit spvId passed via navigation state (e.g. from Deal Record CTA)
  // 2. Co-investor's own SPV — commitment entry whose contextId matches the active context
  // 3. First non-formation SPV
  // 4. First SPV in list
  const requestedSpvId = location.state?.spvId;
  const coInvestorSpv = isCoInvestor
    ? MOCK_SPVS.find((s) => s.commitments.some((c) => c.contextId === activeContext?.id))
    : null;
  const activeSPV =
    MOCK_SPVS.find((s) => s.id === requestedSpvId) ??
    coInvestorSpv ??
    MOCK_SPVS.find((s) => s.status !== "pre-formation") ??
    MOCK_SPVS[0];

  // Resolve fund for this SPV — used in breadcrumb
  const activeFund = MOCK_FUNDS.find((f) => f.id === activeSPV?.fundId);

  // Resolve isSelf via contextId — no string-matching against entity name needed
  const contextEntity = activeContext?.entity;
  const commitments = activeSPV.commitments.map((c) => ({
    ...c,
    isSelf: isCoInvestor ? c.contextId === activeContext?.id : false,
  }));

  const [tasks, setTasks] = useState(activeSPV.tasks);
  const [showExport, setShowExport] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [callAcknowledged, setCallAcknowledged] = useState(false);

  const addToast = useCallback((type, title, message) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, type, title, message }]);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev?.filter((t) => t?.id !== id));
  }, []);

  const handleTaskStatusChange = (taskId) => {
    setTasks((prev) =>
      prev?.map((t) => {
        if (t?.id !== taskId) return t;
        const next = t?.status === "done" ? "todo" : "done";
        addToast("success", "Task Updated", `Task marked as ${next === "done" ? "complete" : "to do"}.`);
        return { ...t, status: next };
      })
    );
  };

  const handleCapitalCall = () => {
    addToast("info", "Capital Call Initiated", "Draft capital call notice created for pending investors.");
  };

  const handleUpload = () => {
    addToast("info", "Upload", "Document upload functionality coming soon.");
  };

  const handleDocPreview = (doc) => {
    addToast("info", "Document Preview", `Opening ${doc?.name}…`);
  };

  const handleShare = () => {
    addToast("success", "Link Copied", "Secure investor link copied to clipboard.");
  };

  const handleExport = () => {
    setShowExport(true);
  };

  const handleAddTask = () => {
    addToast("info", "Add Task", "Task creation panel coming soon.");
  };

  // Derived metrics
  const totalCalled = commitments?.reduce((s, c) => s + c?.called, 0);
  const totalDistributed = commitments?.reduce((s, c) => s + c?.distributed, 0);
  const confirmedCount = commitments?.filter((c) => c?.status === "confirmed")?.length;

  const fmtM = (n) => `£${(n / 1_000_000).toFixed(1)}m`;

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        {/* SPV Header */}
        <SPVHeader
          spv={activeSPV}
          onExport={handleExport}
          onShare={handleShare}
          isCoInvestor={isCoInvestor}
          contextEntity={contextEntity}
          fundId={activeSPV?.fundId}
          fundShortName={activeFund?.shortName}
        />

        {/* Fund-as-entity context strip */}
        {!isCoInvestor && (
          <div
            className="flex items-center gap-3 px-6 md:px-10 lg:px-12 py-2.5 border-b border-border"
            style={{ background: 'rgba(59,76,76,0.03)' }}
          >
            <Icon name="Building2" size={14} color="var(--color-muted-foreground)" />
            <span className="font-caption text-xs text-muted-foreground">
              Acting on behalf of:{' '}
              <span className="font-semibold text-foreground">{activeContext?.entity || 'Greenfield Renewables Fund I'}</span>
              {' · '}Limited Partnership
            </span>
            <button
              className="font-caption text-xs ml-1 transition-colors hover:underline"
              style={{ color: 'var(--color-primary)' }}
              onClick={() => addToast('info', 'Entity Switching', 'Entity switching available in Phase 2.')}
            >
              Switch entity
            </button>
          </div>
        )}

        <div className="px-6 md:px-10 lg:px-12 py-8 space-y-5 md:space-y-6">

          {/* Co-Investor restricted banner */}
          {isCoInvestor && (
            <div
              className="flex items-start gap-3 px-4 py-3 rounded-xl"
              style={{ background: 'rgba(124,58,237,0.07)', border: '1px solid rgba(124,58,237,0.2)' }}
            >
              <Icon name="Eye" size={16} color="#7c3aed" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <p className="font-caption text-sm font-semibold" style={{ color: '#7c3aed' }}>Co-Investor View</p>
                <p className="font-caption text-xs text-muted-foreground mt-0.5">
                  You are viewing your co-investment position only. Capital call, distribution, and structural actions are restricted.
                </p>
              </div>
            </div>
          )}

          {/* IC Approval Pending Banner — spv-tyne pre-formation */}
          {activeSPV.icPendingBanner && (
            <div
              className="flex items-start gap-3 px-4 py-3 rounded-xl"
              style={{ background: 'rgba(217,119,6,0.07)', border: '1px solid rgba(217,119,6,0.25)' }}
            >
              <Icon name="Clock" size={16} color="#d97706" style={{ flexShrink: 0, marginTop: 2 }} />
              <div className="flex-1 min-w-0">
                <p className="font-caption text-sm font-semibold" style={{ color: '#d97706' }}>
                  IC Approval Pending
                </p>
                <p className="font-caption text-xs text-muted-foreground mt-0.5">
                  Anchor investors soft-circled. Formal close opens upon IC decision — IC meeting 13 April 2026.
                </p>
              </div>
              <button
                onClick={() => navigate(ROUTES.icSubmission)}
                className="font-caption text-xs font-semibold px-3 py-1.5 shrink-0 transition-all hover:opacity-90 text-white"
                style={{ background: '#d97706', borderRadius: '6px' }}
              >
                View Submission
              </button>
            </div>
          )}

          {/* Capital Call Banner — renders when capitalCall.status === 'issued' */}
          {activeSPV.capitalCall?.status === 'issued' && (
            <div
              className="flex items-start gap-3 px-4 py-3 rounded-xl"
              style={{ background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.20)' }}
            >
              <Icon name="Bell" size={16} color="#2563eb" style={{ flexShrink: 0, marginTop: 2 }} />
              <div className="flex-1 min-w-0">
                <p className="font-caption text-sm font-semibold" style={{ color: '#2563eb' }}>
                  {isCoInvestor
                    ? `Co-Investment Capital Call No. ${activeSPV.capitalCall.callNumber} — ${fmtM(activeSPV.capitalCall.amount)} due ${new Date(activeSPV.capitalCall.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`
                    : `Capital Call No. ${activeSPV.capitalCall.callNumber} issued ${new Date(activeSPV.capitalCall.issuedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} — ${fmtM(activeSPV.capitalCall.amount)} due ${new Date(activeSPV.capitalCall.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`
                  }
                </p>
                {isCoInvestor ? (
                  <p className="font-caption text-xs text-muted-foreground mt-0.5">
                    Please arrange payment to the GP account and confirm receipt below.
                  </p>
                ) : (
                  <p className="font-caption text-xs text-muted-foreground mt-0.5">
                    Issued to co-investors on {new Date(activeSPV.capitalCall.issuedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} · Status: Active
                  </p>
                )}
              </div>
              {isCoInvestor && !callAcknowledged && (
                <button
                  onClick={() => setCallAcknowledged(true)}
                  className="font-caption text-xs font-semibold px-3 py-1.5 shrink-0 transition-all hover:opacity-90 text-white"
                  style={{ background: '#2563eb', borderRadius: '6px' }}
                >
                  Confirm Receipt
                </button>
              )}
              {isCoInvestor && callAcknowledged && (
                <div className="flex items-center gap-1.5 shrink-0">
                  <Icon name="CheckCircle2" size={15} color="#059669" />
                  <span className="font-caption text-xs font-semibold" style={{ color: '#059669' }}>Receipt Confirmed</span>
                </div>
              )}
            </div>
          )}

          {/* Quick Actions */}
          <QuickActionsPanel context="spv-dashboard" userRole={role} layout="horizontal" />

          {/* Metric Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 lg:gap-5">
            <MetricCard
              label="Total Commitments"
              value={fmtM(activeSPV.raisedAmount)}
              sub={`of £${(activeSPV.targetSize / 1_000_000).toFixed(0)}m target`}
              icon="PoundSterling"
              trend={5.2}
              trendLabel="+5.2%"
              accentColor="var(--color-primary)"
            />
            <MetricCard
              label="Capital Called"
              value={fmtM(totalCalled)}
              sub={activeSPV.raisedAmount > 0 ? `${((totalCalled / activeSPV.raisedAmount) * 100).toFixed(1)}% of commitments` : 'No commitments yet'}
              icon="ArrowUpCircle"
              accentColor="var(--color-secondary)"
            />
            <MetricCard
              label="Distributions"
              value={fmtM(totalDistributed)}
              sub={commitments.length > 0 ? `Across ${commitments.filter((c) => c.distributed > 0).length} investors` : 'No distributions yet'}
              icon="ArrowDownCircle"
              trend={totalDistributed > 0 ? 12.0 : null}
              trendLabel="+12%"
              accentColor="var(--color-success)"
            />
            <MetricCard
              label="Active Investors"
              value={`${confirmedCount} / ${commitments.length}`}
              sub="Capital Commitments"
              icon="Users"
              accentColor="var(--color-secondary)"
            />
          </div>

          {/* Closing Progress */}
          <ClosingProgress
            raised={activeSPV.raisedAmount}
            target={activeSPV.targetSize}
            milestones={activeSPV.milestones}
            isCoInvestor={isCoInvestor}
          />

          {/* SPV Info Strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {[
              { label: "Sector", value: activeSPV.sector, icon: "Zap" },
              { label: "Target IRR", value: activeSPV.targetIRR, icon: "TrendingUp" },
              { label: "Closing Date", value: activeSPV.closingDate, icon: "Calendar" },
              { label: "Vehicle Type", value: activeSPV.vehicleType, icon: "Briefcase" },
            ]?.map((item) => (
              <div key={item?.label} className="card py-3 px-4 flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(59,76,76,0.08)" }}
                >
                  <Icon name={item?.icon} size={16} color="var(--color-primary)" />
                </div>
                <div className="min-w-0">
                  <p className="font-caption text-xs text-muted-foreground">{item?.label}</p>
                  <p className="font-caption text-sm font-semibold text-foreground truncate">{item?.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Asset Overview Panel */}
          {activeSPV.assetSnapshot ? (
            <div className="card px-6 py-5">
              <div className="flex items-center justify-between mb-4">
                <p className="font-caption text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <Icon name="Building2" size={13} color="var(--color-primary)" />
                  Underlying Asset
                </p>
                {!isCoInvestor && activeSPV.assetDataId && (
                  <button
                    onClick={() => navigate(`${ROUTES.portfolioAssets}?id=${activeSPV.assetDataId}`)}
                    className="font-caption text-xs font-semibold hover:underline"
                    style={{ color: 'var(--color-primary)' }}
                  >
                    View Asset Detail →
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-6 items-start">
                <div>
                  <p className="font-heading font-semibold text-foreground text-base">{activeSPV.assetSnapshot.name}</p>
                  <p className="font-caption text-xs text-muted-foreground mt-0.5">{activeSPV.assetSnapshot.sector} · {activeSPV.assetSnapshot.geography}</p>
                </div>
                <div className="flex gap-5 flex-wrap ml-auto">
                  {[
                    { label: 'Status',     value: activeSPV.assetSnapshot.operationalStatus },
                    { label: 'IRR',        value: activeSPV.assetSnapshot.actualIRR ? `${activeSPV.assetSnapshot.actualIRR}% actual` : `${activeSPV.assetSnapshot.targetIRR} target` },
                    { label: 'Valuation',  value: activeSPV.assetSnapshot.valuation },
                    { label: 'ESG Score',  value: activeSPV.assetSnapshot.esgScore ? `${activeSPV.assetSnapshot.esgScore}/100` : 'Pending' },
                    { label: 'Risk Score', value: activeSPV.assetSnapshot.riskScore ? `${activeSPV.assetSnapshot.riskScore}/5` : '—' },
                  ].map(({ label, value }) => (
                    <div key={label} className="text-center">
                      <p className="font-caption text-xs text-muted-foreground">{label}</p>
                      <p className="font-caption text-sm font-semibold text-foreground">{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : activeSPV.assetDataId ? (
            <div className="card px-6 py-4">
              <p className="font-caption text-xs text-muted-foreground flex items-center gap-2">
                <Icon name="Building2" size={13} color="var(--color-muted-foreground)" />
                <span className="font-semibold text-foreground">
                  {MOCK_ASSETS.find(a => a.dealId === activeSPV.dealId)?.label ?? 'Underlying Asset'}
                </span>
                · Asset not yet acquired — pending SPV formation
              </p>
            </div>
          ) : null}

          {/* Three-Panel Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
            {/* Commitments */}
            <div className="lg:col-span-1" style={{ minHeight: "480px" }}>
              <CommitmentsPanel
                commitments={isCoInvestor ? commitments.filter((c) => c.isSelf) : commitments}
                onCapitalCall={isCoInvestor ? null : handleCapitalCall}
              />
            </div>

            {/* Documents */}
            <div className="lg:col-span-1" style={{ minHeight: "480px" }}>
              <DocumentsPanel
                documents={activeSPV.documents}
                onPreview={handleDocPreview}
                onUpload={handleUpload}
                isCoInvestor={isCoInvestor}
              />
            </div>

            {/* Tasks */}
            <div className="lg:col-span-1" style={{ minHeight: "480px" }}>
              <TasksPanel
                tasks={tasks}
                onStatusChange={handleTaskStatusChange}
                onAddTask={handleAddTask}
                isCoInvestor={isCoInvestor}
              />
            </div>
          </div>

          {/* Footer Nav */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2 pb-4 border-t border-border">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="ghost"
                size="sm"
                iconName="LayoutDashboard"
                iconPosition="left"
                iconSize={14}
                onClick={() => navigate(ROUTES.dashboard)}
              >
                Portfolio Dashboard
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="TrendingUp"
                iconPosition="left"
                iconSize={14}
                onClick={() => navigate(ROUTES.opportunities)}
              >
                Opportunities
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="UserCheck"
                iconPosition="left"
                iconSize={14}
                onClick={() => navigate(ROUTES.investorOnboarding)}
              >
                Investor Onboarding
              </Button>
            </div>
            <p className="font-caption text-xs text-muted-foreground">
              SeedDrive Infrastructure Equity · FCA Regulated · © {new Date()?.getFullYear()}
            </p>
          </div>
        </div>
      </div>
      {/* Export Modal */}
      {showExport && <ExportModal spv={activeSPV} onClose={() => setShowExport(false)} />}
      {/* Toast Notifications */}
      <ToastNotification toasts={toasts} onDismiss={dismissToast} />
    </AppLayout>
  );
}
